import React, { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import { FindCareModule } from '@premkumarmph/find-care';
import { GRAPHQL } from '../../../config/api_end_point';
import { auth } from '../../components/Routes';
import { withStyles } from '@mui/styles';
import '../../../src/assets/styles/style.css';
import useClickFooter from '../../hooks/useClickFooter';
import { useLocation, useSearchParams } from 'react-router-dom';
import SearchForm from "../../features/dynamicHome/components/FindCare/FindCareNew/SearchForm"
import SearchResults from "../../features/dynamicHome/components/FindCare/FindCareNew/SearchResults"
import ProviderDetails from "../../features/dynamicHome/components/FindCare/FindCareNew/ProviderDetails"
import { fetchPlanUidByNetworkUnauth } from "../../features/dynamicHome/components/FindCare/FindCareNew/providers-api"
import BreadCrumb from "../../components/molecules/BreadCrumb"
import cignaLogo from '../../assets/images/cigna-logo.png';
import medcostLogo from '../../assets/images/Medcost.png';
import mmoLogo from '../../assets/images/mmo.png';

const styles = () => ({
  container: {
    backgroundColor: '#F5F5F5',
    minHeight: '100vh',
    padding: '0px',
    marginLeft: '32px'
  },
  title: {
    fontSize: 28,
    fontWeight: 600,
    marginTop: 24,
    color: '#1B1B1B',
  },
  subTitle: {
    fontFamily: 'Inter',
    fontSize: '16px',
    fontWeight: 600,
    color: '#1B1B1B',
    marginTop: 40,
  },
  billCardContainer: {
    display: 'flex',
    marginTop: 12,
  },
  firstCard: {
    display: 'flex',
    backgroundColor: '#ffffff',
    borderRadius: 16,
    boxShadow: '0px 2px 6px rgba(0,0,0,0.15)',
  },
  secondCard: {
    display: 'flex',
    backgroundColor: '#ffffff',
    borderRadius: 16,
    boxShadow: '0px 2px 6px rgba(0,0,0,0.15)',
  },
  contentSection: {
    height: 160,
    width: 340,
    position: 'relative',
  },
  cardTitle: {
    paddingTop: 20,
    fontFamily: 'Inter',
    fontStyle: 'normal',
    fontWeight: 600,
    fontSize: 18,
    paddingLeft: 30,
    lineHeight: ' 20px',
    color: '#101828',
  },
  cardContent: {
    paddingTop: 12,
    fontWeight: 400,
    fontSize: 14,
    color: '#828282',
    paddingLeft: 30,
    width: '50%',
    color: '#667085',
  },
  image: { width: 32, height: 32 },
  noOfBilldDueText: {
    paddingTop: 24,
    fontWeight: 500,
    fontSize: 14,
    paddingLeft: 30,
    lineHeight: ' 20px',
    color: '#667085',
  },
  consentRequestButton: {
    border: '1px solid #A0A4A6',
    color: '#036FCB',
    height: 24,
    padding: '2px 8px 0px',
    borderRadius: 20,
    minWidth: 90,
    margin: '14px 0px 0px 28px',
  },
});

const BASE_URL = process.env.APOLLO_CLIENT;
const API_PATH = GRAPHQL;

const COMPLETE_URL = BASE_URL + API_PATH;
sessionStorage.setItem('FINDCARE_URL', COMPLETE_URL);

const FindCare = ({ classes, ...props }) => {
  useClickFooter(props.onFooterItemClick);
  const token = auth.getAccessToken();
  sessionStorage.setItem('FROM_ENTERPRISE', false);
  const location = useLocation();
  const [searchParams] = useSearchParams();
  
  // Check if this is partner token-based access route
  const partnerToken = localStorage.getItem('findCareToken');

  // Partner configuration
  const PARTNER_DOMAINS = {
    cigna: 'cigna-price-transparency.mpoweredhealth',
    medcost: 'medcost-price-transparency.mpoweredhealth',
    mmo: 'mmo-price-transparency.mpoweredhealth'
  };

  // Check if subscriberId exists in URL params
  const subscriberId = searchParams.get('subscriberId');

  // Detect current partner from localStorage or domain (fallback)
  const detectCurrentPartner = () => {
    const storedPartner = localStorage.getItem('findCarePartner');
    if (storedPartner) return storedPartner.toLowerCase();

    // Fallback: detect from hostname
    const hostname = window.location.hostname || '';
    for (const [partnerName, domain] of Object.entries(PARTNER_DOMAINS)) {
      if (hostname.startsWith(domain)) {
        // Store for future use
        localStorage.setItem('findCarePartner', partnerName);
        return partnerName;
      }
    }
    return null;
  };

  const currentPartner = detectCurrentPartner();

  // URL params — planUid is only present for partner route; public flow fetches it via API
  const urlPlanUid = searchParams.get('planUid');
  // organizationName falls back to currentPartner for public flow (comes from DNS)
  const partnerOrganizationName = searchParams.get('organizationName') || currentPartner;

  // Partner route: has findCareToken
  const isPartnerRoute = location.pathname === '/find-care' && !!partnerToken && !!currentPartner;

  // Public route: no findCareToken and no subscriberId (uses unauth APIs)
  const isPublicRoute = location.pathname === '/find-care' && !partnerToken && !subscriberId;

  // currentNetwork is the same for both partner and public routes — derived from hostname
  const currentNetwork = currentPartner;

  // Logo configuration for both partner and public flows
  const networkLogos = {
    cigna: { src: cignaLogo, alt: 'Cigna' },
    medcost: { src: medcostLogo, alt: 'Medcost' },
    mmo: { src: mmoLogo, alt: 'MMO' }
  };

  // Public flow: fetch planUid from API using the hostname-derived network name
  const [publicPlanUid, setPublicPlanUid] = useState(null)

  useEffect(() => {
    if (isPublicRoute && currentPartner) {
      fetchPlanUidByNetworkUnauth(currentPartner).then((uid) => {
        if (uid) {
          setPublicPlanUid(uid)
          localStorage.setItem('findCarePlanUid', uid)
        }
      })
    }
  }, [isPublicRoute, currentPartner])

  // Partner route: store planUid from URL in localStorage for API calls
  useEffect(() => {
    if (isPartnerRoute && urlPlanUid) {
      localStorage.setItem('findCarePlanUid', urlPlanUid)
    }
  }, [isPartnerRoute, urlPlanUid])

  // Effective planUid: API-fetched for public flow, URL param for partner flow
  const effectivePlanUid = isPublicRoute ? publicPlanUid : urlPlanUid
  // const [ hidePartnerList, setHidePartnerList]= useState(location.state?.hidePartnerList ?? false)
  // useEffect(() => {
  //   if (window.location.hostname.startsWith('patientadvocates.mpowered')) {
  //     setHidePartnerList(true);
  //   }
  // }, []);

  const [currentView, setCurrentView] = useState("search") // 'search', 'results', 'details'
  const [searchCriteria, setSearchCriteria] = useState(null)
  const [selectedProvider, setSelectedProvider] = useState(null)
  const [loading, setLoading] = useState(false)
  // Add state to track current filters
  const [currentFilters, setCurrentFilters] = useState(null)

  const handleSearch = (formData) => {
    setSearchCriteria(formData)
    // Initialize current filters with the search form data
    setCurrentFilters({
      location: formData.location || null,
      provider: formData.provider,
      specialty: formData.specialty,
      distance: 5,
    })
    setCurrentView("results")
  }

  const handleBackToSearch = () => {
    setCurrentView("search")
    setSearchCriteria(null)
    setSelectedProvider(null)
    setCurrentFilters(null)
  }

  // Update to receive and store current filters
  const handleProviderSelect = (provider, criteria, filters) => {
    setSelectedProvider(provider)
    // Keep the original search criteria or use the passed one
    if (criteria && criteria !== searchCriteria) {
      setSearchCriteria(criteria)
    }
    // Store the current filter state
    if (filters) {
      setCurrentFilters(filters)
    }
    setCurrentView("details")
  }

  const handleBackToResults = () => {
    setCurrentView("results")
    setSelectedProvider(null)
  }

  // Add handler to update current filters from SearchResults
  const handleFiltersUpdate = (updatedFilters) => {
    setCurrentFilters(updatedFilters)
  }

  // Generate breadcrumb data based on current view
  const getBreadCrumbProps = () => {
    // Don't show breadcrumb on initial search view for partner or public route
    if (currentView === "search" && (isPartnerRoute || isPublicRoute)) {
      return []
    }

    const baseBreadcrumbs = [];
    
    // Only show Shop breadcrumb if NOT partner or public route
    if (!isPartnerRoute && !isPublicRoute) {
      baseBreadcrumbs.push({ label: 'Shop', route: '/shop' });
    }
    
    baseBreadcrumbs.push({ 
      label: 'Find care', 
      redirectTo: () => handleBackToSearch()
    });

    if (currentView === "results") {
      return [
        ...baseBreadcrumbs,
        { label: 'Search results' }
      ]
    }

    if (currentView === "details" && selectedProvider) {
      return [
        ...baseBreadcrumbs,
        { 
          label: 'Search results', 
          redirectTo: () => handleBackToResults()
        },
        { label: selectedProvider.name }
      ]
    }

    return baseBreadcrumbs
  }

  const showNetworkLogo = (isPartnerRoute || isPublicRoute) && currentNetwork && networkLogos[currentNetwork];
  
  return (
    <div className={classes.container} style={{margin: (isPartnerRoute || isPublicRoute) ? '0 auto' : 0}}>
        {/* Network Logo - shown for both partner and public routes */}
        {showNetworkLogo && (
          <Box sx={{ 
            bgcolor: 'white', 
            width: '100%', 
            p: 1, 
            mb: 1,
            boxShadow: '0px 2px 4px rgba(0,0,0,0.1)'
          }}>
            <img 
              src={networkLogos[currentNetwork].src} 
              alt={networkLogos[currentNetwork].alt} 
              style={{ height: '60px', display: 'block' }} 
            />
          </Box>
        )}
      <Box sx={{ height: "100%", bgcolor: "#f5f5f5", marginLeft: 6 }}>
        
        {/* BreadCrumb - shown on all views */}
        <Box sx={{ pt: 3, pb: 2, pl: 0, pr: 2 }}>
          <BreadCrumb breadCrumbProps={getBreadCrumbProps()} />
        </Box>

        {currentView === "search" && (
          <SearchForm
            onSearch={handleSearch}
            loading={loading}
            isPartnerRoute={isPartnerRoute}
            isPublicRoute={isPublicRoute}
            planUid={effectivePlanUid}
            partnerOrganizationName={partnerOrganizationName}
          />
        )}

        {currentView === "results" && searchCriteria && (
          <SearchResults
            searchCriteria={searchCriteria}
            currentFilters={currentFilters}
            onBack={handleBackToSearch}
            onProviderSelect={handleProviderSelect}
            onFiltersUpdate={handleFiltersUpdate}
            isPublicRoute={isPublicRoute}
          />
        )}

        {currentView === "details" && selectedProvider && searchCriteria && (
          <ProviderDetails
            provider={selectedProvider}
            searchCriteria={searchCriteria}
            onBack={handleBackToResults}
            isPartnerRoute={isPartnerRoute}
            isPublicRoute={isPublicRoute}
          />
        )}
      </Box>
    </div>
  );
};

export default withStyles(styles)(FindCare);
